import React, { PureComponent } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import history from '../../../utils/history';
import styles from './StoreForm.module.scss';
import Input from '../../blocks/Input';
import Spinner from '../../common/Spinner';
import RadioGroup from '../../common/RadioGroup';
import Button from '../../common/Button';
import Select from '../../common/Select';
import Radio from '../../common/Radio';
import { requiredValidator, emailValidator, patternValidator, isFormValid } from '../../../utils/validators';
import { countries, states } from '../../../constants/country';

const validators = [
  {
    key: 'first_name',
    validator: [requiredValidator],
  },
  {
    key: 'last_name',
    validator: [requiredValidator],
  },
  {
    key: 'email',
    validator: [emailValidator],
  },
  {
    key: 'address1',
    validator: [requiredValidator],
  },
  {
    key: 'city',
    validator: [requiredValidator],
  },
  {
    key: 'state',
    validator: [requiredValidator],
  },
  {
    key: 'country',
    validator: [requiredValidator],
  },
  {
    key: 'zip_code',
    validator: [requiredValidator],
  },
];

class StoreForm extends PureComponent {
  poNumberValidator = patternValidator(/^[\d\w-]+$/, "PO number can't contain spaces or special characters");

  constructor(props) {
    super(props);

    this.state = {
      store: 'default',
      ship_to: 'store',
      shipping_address: {
        first_name: '',
        last_name: '',
        address1: '',
        address2: '',
        country: 'US',
        state: 'AL',
        city: '',
        zip_code: '',
        phone_number: '',
        email: '',
        company_name: '',
      },
      po_number: '',
      po_number_validated: true,
    };
  }

  onSubmit = (e) => {
    const { stores, sendOrder, retailerName } = this.props;
    const { store, ship_to, shipping_address, po_number } = this.state;

    const currentStore = stores.find(({ id }) => id === +store);
    const shipping =
      ship_to === 'address'
        ? shipping_address
        : {
            last_name: currentStore.name,
            address1: currentStore.address1,
            address2: currentStore.address2 || '',
            country: currentStore.country,
            state: currentStore.state,
            city: currentStore.city,
            zip_code: currentStore.zip_code,
            phone_number: currentStore.phone_number || '',
            email: currentStore.email || '',
            company_name: retailerName,
          };

    sendOrder(store, shipping, 'UPS_GND', po_number === '' ? null : po_number);
    e.preventDefault();
  };

  handleChange = (event) => {
    const { name, value } = event.target || event;

    this.setState((prevState) => {
      return {
        ...prevState,
        shipping_address: {
          ...prevState.shipping_address,
          [name]: value,
        },
      };
    });
  };

  handleChangeCountry = (value) => {
    const state = states[value][0].value;

    this.setState((prevState) => {
      return {
        ...prevState,
        shipping_address: {
          ...prevState.shipping_address,
          country: value,
          state,
        },
      };
    });
  };

  handleChangeShipTo(value) {
    this.setState({
      ship_to: value,
    });
  }

  handleSelectStore = (value) => {
    if (value === 'add-new') {
      history.push(`/retailers/${this.props.retailerId}/admin`);
    }

    this.setState({ store: value });
  };

  handlePoNumberChange = (event) => {
    const { value } = event.target;

    this.setState({
      po_number: value,
      po_number_validated: !this.poNumberValidator(value).isError,
    });
  };

  isDisabled = () => {
    const { store, ship_to, shipping_address, po_number_validated } = this.state;
    const { hasErrors, isLoading } = this.props;

    if (hasErrors || isLoading || store === 'default' || !po_number_validated) {
      return true;
    }

    if (ship_to === 'address') {
      return !isFormValid(shipping_address, validators);
    }

    return false;
  };

  storesOptions() {
    const { stores, isAdmin } = this.props;
    const options = stores.map((store) => ({ value: store.id, label: store.name }));

    options.unshift({ value: 'default', label: 'Choose a store from the list' });

    if (isAdmin) {
      options.push({ value: 'add-new', label: 'Add a store' });
    }

    return options;
  }

  render() {
    const { isLoading } = this.props;
    const { store, ship_to, shipping_address, po_number, po_number_validated } = this.state;
    const currentStates = states[shipping_address.country];

    return (
      <div className={styles.store}>
        <div className={styles.head}>
          <h3>Order Info</h3>
        </div>
        <div className={styles.wrapper_form}>
          <section className={styles.billToSection}>
            <h4>Bill To Info</h4>
            <div className={styles.select}>
              <label>Store:</label>
              <Select
                onChange={this.handleSelectStore}
                className={styles.select_item}
                value={store}
                options={this.storesOptions()}
              />
            </div>
          </section>

          <section className={styles.shipToSection}>
            <h4>Ship To Info</h4>
            <div className={styles.switches}>
              <div>
                <RadioGroup
                  selectedvalue={ship_to}
                  onChange={(value) => this.handleChangeShipTo(value)}
                  name="store"
                  row
                >
                  <Radio id="1" value="store" label="Store" />
                  <Radio id="2" value="address" label="Address" />
                </RadioGroup>
              </div>
            </div>

            {ship_to === 'address' ? (
              <div className={styles.form}>
                <form>
                  <div className={styles.right_block}>
                    <div>
                      <p>
                        First name: <span>*</span>
                      </p>
                      <Input
                        name="first_name"
                        value={shipping_address.first_name}
                        onChange={this.handleChange}
                        blur
                        validate={[requiredValidator]}
                        autocomplete="given-name"
                      />
                    </div>
                    <div>
                      <p>
                        Last name: <span>*</span>
                      </p>
                      <Input
                        name="last_name"
                        value={shipping_address.last_name}
                        onChange={this.handleChange}
                        blur
                        validate={[requiredValidator]}
                        autocomplete="family-name"
                      />
                    </div>
                    <div>
                      <p>Email:</p>
                      <Input
                        name="email"
                        value={shipping_address.email}
                        onChange={this.handleChange}
                        validate={[emailValidator]}
                        autocomplete="email"
                      />
                    </div>
                    <div>
                      <p>Phone:</p>
                      <Input name="phone_number" value={shipping_address.phone_number} onChange={this.handleChange} />
                    </div>
                    <div>
                      <p>
                        Address 1: <span>*</span>
                      </p>
                      <Input
                        name="address1"
                        value={shipping_address.address1}
                        onChange={this.handleChange}
                        blur
                        validate={[requiredValidator]}
                        autocomplete="address-line1"
                      />
                    </div>
                    <div>
                      <p>Address 2:</p>
                      <Input
                        name="address2"
                        value={shipping_address.address2}
                        onChange={this.handleChange}
                        autocomplete="address-line2"
                      />
                    </div>
                    <div>
                      <p>
                        City: <span>*</span>
                      </p>
                      <Input
                        name="city"
                        value={shipping_address.city}
                        onChange={this.handleChange}
                        autocomplete="home city"
                      />
                    </div>
                    <div>
                      <p>
                        Country: <span>*</span>
                      </p>
                      <Select
                        name="country"
                        value={shipping_address.country}
                        onChange={this.handleChangeCountry}
                        className={styles.form_select}
                        options={countries}
                      />
                    </div>
                    <div>
                      <p>
                        State: <span>*</span>
                      </p>
                      <Select
                        name="state"
                        value={shipping_address.state}
                        onChange={(value) => this.handleChange({ name: 'state', value })}
                        className={styles.form_select}
                        options={currentStates}
                      />
                    </div>
                    <div>
                      <p>
                        Zip Code: <span>*</span>
                      </p>
                      <Input
                        name="zip_code"
                        value={shipping_address.zip_code}
                        onChange={this.handleChange}
                        blur
                        validate={[requiredValidator]}
                        autocomplete="postal-code"
                      />
                    </div>
                  </div>
                </form>
              </div>
            ) : (
              ''
            )}
          </section>

          <section className={styles.poNumberSection}>
            <h4>Purchase Order # (optional)</h4>
            <div>
              <Input
                name="po_number"
                value={po_number}
                onChange={this.handlePoNumberChange}
                blur
                validate={[patternValidator(/^[\d\w-]+$/)]}
              />
              {!po_number_validated && (
                <div className={styles.error}>PO number can't contain spaces or special characters</div>
              )}
            </div>
          </section>

          <div className={cn(styles.submit, isLoading ? styles.spinner : '')}>
            {isLoading ? (
              <Spinner size="small" />
            ) : (
              <Button color="green" text="order" disabled={this.isDisabled()} type="submit" onClick={this.onSubmit} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default StoreForm;

StoreForm.propTypes = {
  stores: PropTypes.array.isRequired,
  sendOrder: PropTypes.func.isRequired,
  hasErrors: PropTypes.bool,
  isLoading: PropTypes.bool,
  retailerId: PropTypes.number.isRequired,
  retailerName: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
};
